// client/src/components/react-dashboard/dashboard/index.jsx

import React, {
  useEffect,
  useState,
} from 'react';

import {
  Alert,
  Box,
  Grid,
} from '@mui/material';

import Header from '../global/Header';

import ATGWidget from './widgets/ATGWidget';
import ChartWidget from './widgets/ChartWidget';
import OrganizationSetupWidget from './widgets/OrganizationSetupWidget';

import OrganizationSetup from '../org_form/OrganizationSetup';

import useAuth from '../../../hooks/useAuth';

import {
  useGetMyOrganizationSetupQuery,
} from '../../../features/organizations/organizationsApiSlice';


const Dashboard = () => {
  const {
    role,
    organization_id,
  } = useAuth();


  const [
    organizationSetupOpen,
    setOrganizationSetupOpen,
  ] = useState(false);


  /*
   * Purchased ATG Pro account owners are
   * Managers and have a real organization ID.
   *
   * Don't make admin/system accounts hit the
   * self-service setup endpoint.
   */
  const shouldCheckOrganizationSetup =
    role === 'Manager' &&
    Boolean(organization_id) &&
    organization_id !== 'admin';


  const {
    data: organization,

    isLoading:
      isOrganizationSetupLoading,

    isError:
      isOrganizationSetupError,

    error:
      organizationSetupError,

    refetch:
      refetchOrganizationSetup,
  } =
    useGetMyOrganizationSetupQuery(
      undefined,
      {
        skip:
          !shouldCheckOrganizationSetup,
      }
    );


  /*
   * IMPORTANT:
   *
   * Only explicit false means this organization
   * needs the new setup workflow.
   *
   * This prevents older organizations that don't
   * have setupComplete at all from suddenly being
   * treated as incomplete.
   */
  const needsOrganizationSetup =
    organization?.setupComplete ===
    false;


  /*
   * Automatically show the modal when a Manager
   * enters the dashboard with an incomplete org.
   *
   * If they close/skip it, remember that for this
   * browser tab/session so it doesn't repeatedly
   * pop open during the same session.
   *
   * The setup widget remains visible regardless.
   */
  useEffect(() => {
    if (
      !needsOrganizationSetup ||
      !organization?.organization_id
    ) {
      return;
    }

    const storageKey =
      `usfuel-org-setup-skipped-${organization.organization_id}`;

    const skippedThisSession =
      sessionStorage.getItem(
        storageKey
      ) === 'true';

    if (!skippedThisSession) {
      setOrganizationSetupOpen(
        true
      );
    }
  }, [
    needsOrganizationSetup,
    organization?.organization_id,
  ]);


  const handleCloseOrganizationSetup =
    () => {
      if (
        organization?.organization_id
      ) {
        sessionStorage.setItem(
          `usfuel-org-setup-skipped-${organization.organization_id}`,
          'true'
        );
      }

      setOrganizationSetupOpen(
        false
      );
    };


  const handleOpenOrganizationSetup =
    () => {
      setOrganizationSetupOpen(
        true
      );
    };


  const handleOrganizationSetupCompleted =
    async () => {
      if (
        organization?.organization_id
      ) {
        sessionStorage.removeItem(
          `usfuel-org-setup-skipped-${organization.organization_id}`
        );
      }

      /*
       * OrganizationSetup already refreshes
       * the JWT after PATCH succeeds.
       *
       * Now refresh this actual organization
       * query so setupComplete becomes true
       * here and the widget disappears.
       */
      await refetchOrganizationSetup();

      setOrganizationSetupOpen(
        false
      );
    };


  return (
    <Box m="20px">

      <Box
        display="flex"
        justifyContent="space-between"
        alignItems="center"
      >
        <Header
          title="DASHBOARD"
          subtitle="Welcome to the US Fuel Pro Console."
        />
      </Box>


      <Grid
        container
        spacing={4}
      >

        {/*
         * ───────────────────────────────────
         * ORGANIZATION SETUP
         * ───────────────────────────────────
         *
         * Always first when needed.
         */}
        {needsOrganizationSetup && (
          <Grid
            item
            xs={12}
          >
            <OrganizationSetupWidget
              organization={
                organization
              }

              onOpen={
                handleOpenOrganizationSetup
              }
            />
          </Grid>
        )}


        {/*
         * Optional error feedback.
         *
         * This only appears for Managers for whom
         * we expected organization setup data.
         */}
        {shouldCheckOrganizationSetup &&
          isOrganizationSetupError && (
            <Grid
              item
              xs={12}
            >
              <Alert
                severity="error"
              >
                Unable to load your organization setup status.
                {' '}
                {organizationSetupError
                  ?.data
                  ?.message ||
                  ''}
              </Alert>
            </Grid>
          )}


        {/*
         * ───────────────────────────────────
         * EXISTING DASHBOARD WIDGETS
         * ───────────────────────────────────
         */}

        <Grid
          item
          xs={12}
          md={6}
        >
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <ATGWidget />
          </Box>
        </Grid>


        <Grid
          item
          xs={12}
          md={6}
        >
          <Box
            sx={{
              height: '100%',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <ChartWidget />
          </Box>
        </Grid>

      </Grid>


      {/*
       * ─────────────────────────────────────
       * ORGANIZATION SETUP MODAL
       * ─────────────────────────────────────
       */}

      {needsOrganizationSetup && (
        <OrganizationSetup
          open={
            organizationSetupOpen
          }

          organization={
            organization
          }

          onClose={
            handleCloseOrganizationSetup
          }

          onCompleted={
            handleOrganizationSetupCompleted
          }
        />
      )}

    </Box>
  );
};


export default Dashboard;